/**
 * Redux middleware for persisting monitoring state to localStorage
 * Saves and restores filmstrip data and ATR thumbnails across browser sessions
 */

const MONITORING_STORAGE_KEY = 'leader-ui-monitoring-state';
const STORAGE_VERSION = '2.0';

// Actions that should trigger persistence
const PERSIST_ACTIONS = [
  'monitoring/setFilmstripState',
  'monitoring/clearFilmstripState',
  'monitoring/clearVideoMonitoringData',
  'monitoring/clearAllMonitoringData',
  'monitoring/setATRThumbnails',
  'monitoring/setATRSourceActivity',
  'monitoring/clearATRMonitoringData'
];

/**
 * Load monitoring state from localStorage
 */
export const loadMonitoringState = () => {
  try {
    const serializedState = localStorage.getItem(MONITORING_STORAGE_KEY);
    if (serializedState === null) {
      return undefined; // Let Redux use initial state
    }

    const parsedState = JSON.parse(serializedState);

    // Check version compatibility
    if (parsedState.version !== STORAGE_VERSION) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Monitoring persistence: Version mismatch, clearing stored state');
      }
      localStorage.removeItem(MONITORING_STORAGE_KEY);
      return undefined;
    }

    // Clean up old entries (older than 2 hours)
    const now = Date.now();
    const maxAge = 2 * 60 * 60 * 1000; // 2 hours

    // Clean filmstrip state
    const cleanedFilmstripState = {};
    Object.entries(parsedState.filmstripState || {}).forEach(([sourceId, state]) => {
      if (state.lastUpdated && (now - state.lastUpdated) < maxAge) {
        cleanedFilmstripState[sourceId] = state;
      }
    });

    // Clean ATR thumbnails (keep thumbnails from last 2 hours)
    const cleanedATRThumbnails = {};
    Object.entries(parsedState.atrThumbnails || {}).forEach(([sourceId, thumbnails]) => {
      if (Array.isArray(thumbnails)) {
        const recentThumbnails = thumbnails.filter(thumb =>
          thumb.timestamp && (now - thumb.timestamp) < maxAge
        );
        if (recentThumbnails.length > 0) {
          cleanedATRThumbnails[sourceId] = recentThumbnails;
        }
      }
    });

    // Clean ATR source activity
    const cleanedATRSourceActivity = {};
    Object.entries(parsedState.atrSourceActivity || {}).forEach(([sourceId, activity]) => {
      if (activity.lastSeen && (now - activity.lastSeen) < maxAge) {
        cleanedATRSourceActivity[sourceId] = activity;
      }
    });

    if (process.env.NODE_ENV === 'development') {
      console.log(`Monitoring persistence: Loaded state - filmstrip: ${Object.keys(cleanedFilmstripState).length} sources, ATR thumbnails: ${Object.keys(cleanedATRThumbnails).length} sources, ATR activity: ${Object.keys(cleanedATRSourceActivity).length} sources`);
    }

    return {
      filmstripState: cleanedFilmstripState,
      atrThumbnails: cleanedATRThumbnails,
      atrSourceActivity: cleanedATRSourceActivity,
      lastATRUpdateTime: parsedState.lastATRUpdateTime || null
    };
  } catch (error) {
    console.error('Monitoring persistence: Error loading state from localStorage:', error);
    localStorage.removeItem(MONITORING_STORAGE_KEY);
    return undefined;
  }
};

// Keep the old function name for backward compatibility
export const loadFilmstripState = loadMonitoringState;

// Track last saved state to prevent unnecessary saves
let lastSavedStateHash = null;

/**
 * Save monitoring state to localStorage
 */
const saveMonitoringState = (monitoringState) => {
  try {
    const { filmstripState, atrThumbnails, atrSourceActivity, lastATRUpdateTime } = monitoringState;

    // Create a more detailed hash of the current state to check if it has changed
    const currentStateHash = JSON.stringify({
      filmstripState: Object.keys(filmstripState || {}).sort(),
      atrThumbnails: Object.entries(atrThumbnails || {}).map(([k, v]) => [k, Array.isArray(v) ? v.length : 0]).sort(),
      atrSourceActivity: Object.entries(atrSourceActivity || {}).map(([k, v]) => [k, v?.lastSeen || 0]).sort(),
      lastATRUpdateTime
    });

    // Skip save if state hasn't changed
    if (currentStateHash === lastSavedStateHash) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Monitoring persistence: Skipping save, state unchanged');
      }
      return;
    }

    // Only save non-empty filmstrip states
    const filmstripStatesToSave = {};
    Object.entries(filmstripState || {}).forEach(([sourceId, state]) => {
      if (state) {
        let hasRealThumbnails = false;

        // Check for ATR filmstrip format (allThumbnails/displayedThumbnails)
        if (state.allThumbnails && state.allThumbnails.length > 0) {
          const realThumbnails = state.allThumbnails.filter(item => !item.isPlaceholder);
          if (realThumbnails.length > 0) {
            hasRealThumbnails = true;
          }
        }
        // Check for regular filmstrip format (filmstripData)
        else if (state.filmstripData && state.filmstripData.length > 0) {
          const realThumbnails = state.filmstripData.filter(item => !item.isPlaceholder);
          if (realThumbnails.length > 0) {
            hasRealThumbnails = true;
          }
        }

        if (hasRealThumbnails) {
          filmstripStatesToSave[sourceId] = {
            ...state,
            // Keep all data including placeholders for proper positioning
            filmstripData: state.filmstripData,
            allThumbnails: state.allThumbnails,
            displayedThumbnails: state.displayedThumbnails
          };
        }
      }
    });

    // Only save non-empty ATR thumbnails
    const atrThumbnailsToSave = {};
    Object.entries(atrThumbnails || {}).forEach(([sourceId, thumbnails]) => {
      if (Array.isArray(thumbnails) && thumbnails.length > 0) {
        atrThumbnailsToSave[sourceId] = thumbnails;
      }
    });

    // Only save active ATR source activity
    const atrSourceActivityToSave = {};
    Object.entries(atrSourceActivity || {}).forEach(([sourceId, activity]) => {
      if (activity && activity.lastSeen) {
        atrSourceActivityToSave[sourceId] = activity;
      }
    });

    const stateToSave = {
      version: STORAGE_VERSION,
      timestamp: Date.now(),
      filmstripState: filmstripStatesToSave,
      atrThumbnails: atrThumbnailsToSave,
      atrSourceActivity: atrSourceActivityToSave,
      lastATRUpdateTime: lastATRUpdateTime
    };

    const serializedState = JSON.stringify(stateToSave);
    localStorage.setItem(MONITORING_STORAGE_KEY, serializedState);

    // Update the saved state hash
    lastSavedStateHash = currentStateHash;

    if (process.env.NODE_ENV === 'development') {
      console.log(`Monitoring persistence: Saved state - filmstrip: ${Object.keys(filmstripStatesToSave).length} sources, ATR thumbnails: ${Object.keys(atrThumbnailsToSave).length} sources, ATR activity: ${Object.keys(atrSourceActivityToSave).length} sources`);
    }
  } catch (error) {
    console.error('Monitoring persistence: Error saving state to localStorage:', error);
    // If storage is full or other error, try to clear old data
    try {
      localStorage.removeItem(MONITORING_STORAGE_KEY);
    } catch (clearError) {
      console.error('Monitoring persistence: Error clearing localStorage:', clearError);
    }
  }
};

/**
 * Redux middleware for monitoring persistence
 */
const monitoringPersistenceMiddleware = (store) => (next) => (action) => {
  // Call the next middleware/reducer first
  const result = next(action);

  // Check if this action should trigger persistence
  if (PERSIST_ACTIONS.includes(action.type)) {
    // Prevent excessive saves by checking if we're already in a save cycle
    if (monitoringPersistenceMiddleware.isSaving) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Monitoring persistence: Skipping save, already in progress');
      }
      return result;
    }

    const state = store.getState();
    const monitoringState = {
      filmstripState: state.monitoring?.filmstripState,
      atrThumbnails: state.monitoring?.atrThumbnails,
      atrSourceActivity: state.monitoring?.atrSourceActivity,
      lastATRUpdateTime: state.monitoring?.lastATRUpdateTime
    };

    // Debounce saves to avoid excessive localStorage writes
    if (monitoringPersistenceMiddleware.saveTimeout) {
      clearTimeout(monitoringPersistenceMiddleware.saveTimeout);
    }

    monitoringPersistenceMiddleware.saveTimeout = setTimeout(() => {
      // Set flag to prevent circular saves
      monitoringPersistenceMiddleware.isSaving = true;

      try {
        saveMonitoringState(monitoringState);
      } catch (error) {
        console.error('Monitoring persistence: Error during save:', error);
      } finally {
        // Always clear the flag
        monitoringPersistenceMiddleware.isSaving = false;
      }
    }, 1000); // Save after 1 second of inactivity
  }

  return result;
};

// Keep the old name for backward compatibility
const filmstripPersistenceMiddleware = monitoringPersistenceMiddleware;

export default filmstripPersistenceMiddleware;
